const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;
const MS_PER_WEEK = 7 * MS_PER_DAY;

export function getTodayYYYYMMDD(): string {
  const now = new Date();
  const yyyy = String(now.getFullYear());
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function toTimestampMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

export function formatTimeLabel(value: string): string {
  const ms = toTimestampMs(value);
  if (ms === null) return value;

  try {
    return new Date(ms).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

export function toDayKeyYYYYMMDD(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = value.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : null;
}

const DAY_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

function startOfDayMs(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function formatDayLabel(dayKey: string | null, nowMs: number = Date.now()): string {
  if (dayKey === null || !DAY_KEY_RE.test(dayKey)) return 'Unknown date';

  const [year = NaN, month = NaN, day = NaN] = dayKey.split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return 'Unknown date';
  }

  const target = new Date(year, month - 1, day);
  if (Number.isNaN(target.getTime())) return 'Unknown date';

  const now = new Date(nowMs);
  const todayMs = startOfDayMs(now);
  const targetMs = startOfDayMs(target);
  const deltaDays = Math.round((todayMs - targetMs) / MS_PER_DAY);

  if (deltaDays === 0) return 'Today';
  if (deltaDays === 1) return 'Yesterday';

  if (deltaDays > 1 && deltaDays < 7) {
    return target.toLocaleDateString(undefined, { weekday: 'long' });
  }

  const sameYear = target.getFullYear() === now.getFullYear();
  return target.toLocaleDateString(
    undefined,
    sameYear
      ? { month: 'short', day: '2-digit' }
      : { month: 'short', day: '2-digit', year: 'numeric' }
  );
}

export function formatRelativeAgeLabel(
  value: string | null | undefined,
  nowMs: number = Date.now()
): string | null {
  const valueMs = toTimestampMs(value);
  if (valueMs === null) return null;

  const ageMs = Math.max(0, nowMs - valueMs);

  if (ageMs < MS_PER_MINUTE) {
    const seconds = Math.floor(ageMs / MS_PER_SECOND);
    return seconds === 1 ? '1 second old' : `${seconds} seconds old`;
  }
  if (ageMs < MS_PER_HOUR) {
    const minutes = Math.floor(ageMs / MS_PER_MINUTE);
    return minutes === 1 ? '1 minute old' : `${minutes} minutes old`;
  }
  if (ageMs < MS_PER_DAY) {
    const hours = Math.floor(ageMs / MS_PER_HOUR);
    return hours === 1 ? '1 hour old' : `${hours} hours old`;
  }

  const days = Math.floor(ageMs / MS_PER_DAY);
  return days === 1 ? '1 day old' : `${days} days old`;
}

export function formatRelativeTimeShort(
  timestampMs: number | null | undefined,
  nowMs: number = Date.now()
): string | null {
  if (timestampMs == null || !Number.isFinite(timestampMs)) return null;

  const ageMs = Math.max(0, nowMs - timestampMs);

  if (ageMs < MS_PER_MINUTE) return 'just now';
  if (ageMs < MS_PER_HOUR) return `${Math.floor(ageMs / MS_PER_MINUTE)}m ago`;
  if (ageMs < MS_PER_DAY) return `${Math.floor(ageMs / MS_PER_HOUR)}h ago`;
  if (ageMs < MS_PER_WEEK) return `${Math.floor(ageMs / MS_PER_DAY)}d ago`;

  const weeks = Math.floor(ageMs / MS_PER_WEEK);
  return weeks <= 4 ? `${weeks}w ago` : `${Math.floor(ageMs / (MS_PER_DAY * 30))}mo ago`;
}
